<script lang="ts">
	import { t } from 'svelte-i18n'
	import type {
		AbstractResource,
		OutputObject
	} from '@semanticedge/persistence-service-noheaders-client/src'
	import { OutputObjectModal, OutputObjectsTable, PageHeader } from '@repo/commons/components'
	import type { ProjectViewModel, TableData, ValidatorFunctionFactory } from '@repo/commons/models'
	import {
		confirmDelete,
		DebouncedSearch,
		injectTranslation,
		showSaveFailedToast
	} from '@repo/commons/utils'

	type Props = {
		loading?: boolean
		sortable?: boolean
		project: ProjectViewModel | null
		outputObjects: readonly AbstractResource[]
		onDelete: (outputObjectId: string) => Promise<boolean>
		onAdd: (outputObject: Partial<OutputObject>) => Promise<boolean>
		onEdit: (outputObject: Partial<OutputObject>) => Promise<boolean>
		nameReadOnly?: boolean
		createNameValidator?: (initialName?: string) => ValidatorFunctionFactory
	}

	const {
		outputObjects,
		project,
		loading = false,
		sortable = false,
		onAdd,
		onEdit,
		onDelete,
		nameReadOnly = false,
		createNameValidator
	}: Props = $props()

	const search = new DebouncedSearch()

	let editingOutputObject = $state<TableData | null>(null)
	let isOutputObjectModalVisible = $state(false)

	const outputObjectRows: TableData[] = $derived(
		outputObjects.map((resource) => ({ ...resource, name: resource.name ?? '' }))
	)

	const filteredOutputObjectRows = $derived(search.filterRows(outputObjectRows, ({ name }) => name))

	const existingOutputObjectIds = $derived(outputObjectRows.map(({ id }) => id))

	const emptyText = $derived(
		outputObjectRows.length ? undefined : $t('InstanceOutputObjects.empty')
	)

	const openAddOutputObjectModal = (): void => {
		editingOutputObject = null
		isOutputObjectModalVisible = true
	}

	const openEditOutputObjectModal = (row: TableData): void => {
		editingOutputObject = row
		isOutputObjectModalVisible = true
	}

	const closeOutputObjectModal = (): void => {
		editingOutputObject = null
		isOutputObjectModalVisible = false
	}

	const saveOutputObject = async (
		outputObject: Partial<OutputObject>,
		isEdit: boolean
	): Promise<boolean> => {
		const isSaved = await (isEdit ? onEdit(outputObject) : onAdd(outputObject))

		if (!isSaved) showSaveFailedToast()

		return isSaved
	}

	const confirmAndDeleteOutputObject = async ({ id, name }: TableData): Promise<void> => {
		const isConfirmed = await confirmDelete(
			injectTranslation('Confirmation.deleteTitle', { item: $t('Item.outputObject') }),
			injectTranslation('Confirmation.deleteMessage', {
				item: $t('Item.outputObject'),
				name: name || id
			})
		)

		if (!isConfirmed) return

		const isDeleted = await onDelete(id)

		if (!isDeleted) showSaveFailedToast()
	}
</script>

<div class="content">
	<PageHeader
		addButtonIcon={null}
		onAdd={openAddOutputObjectModal}
		onSearch={search.updateQuery}
		disableAddButton={loading || !project}
		title={$t('InstanceOutputObjects.title')}
		addButtonText={$t('InstanceOutputObjects.add')}
	/>

	<OutputObjectsTable
		{loading}
		{sortable}
		{emptyText}
		rows={filteredOutputObjectRows}
		onEdit={openEditOutputObjectModal}
		onDelete={confirmAndDeleteOutputObject}
	/>
</div>

<OutputObjectModal
	{project}
	onclose={closeOutputObjectModal}
	{editingOutputObject}
	{existingOutputObjectIds}
	visible={isOutputObjectModalVisible}
	onSubmit={saveOutputObject}
	{nameReadOnly}
	{createNameValidator}
/>

<style lang="postcss">
	.content {
		@apply flex flex-col gap-4;
	}
</style>
